import { createSlice } from "@reduxjs/toolkit";

import axios from "../../utils/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL_V2;

const initialState = {
  isLoading: false,
  isSaving: false,
  error: null,
  promotions: [],
  promotion: null,
  page: 1,
  limit: 10,
  totalPages: 0,
  totalResults: 0,
};

function getErrorMessage(error, fallback) {
  if (typeof error === "string") {
    return error;
  }

  return error?.message || error?.error || fallback;
}

const slice = createSlice({
  name: "franchisePromotion",

  initialState,

  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },

    startSaving(state) {
      state.isSaving = true;
      state.error = null;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;

      state.error = action.payload;
    },

    getPromotionsSuccess(state, action) {
      state.isLoading = false;

      state.promotions = action.payload.results || [];

      state.page = action.payload.page || 1;

      state.limit = action.payload.limit || 10;

      state.totalPages = action.payload.totalPages || 0;

      state.totalResults = action.payload.totalResults || 0;
    },

    getPromotionSuccess(state, action) {
      state.isLoading = false;

      state.promotion = action.payload;
    },

    savePromotionSuccess(state, action) {
      state.isSaving = false;

      if (action.payload) {
        state.promotion = action.payload;
      }
    },

    clearPromotion(state) {
      state.promotion = null;
      state.error = null;
    },
  },
});

export const { clearPromotion } = slice.actions;

export default slice.reducer;

export function getFranchisePromotions({
  franchiseId,
  page = 1,
  limit = 1000,
  name,
  isActive,
  venueId,
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`${backendUrl}/franchise/promotion`, {
        params: {
          franchiseId,
          page,
          limit,
          ...(name ? { name } : {}),
          ...(typeof isActive === "boolean" ? { isActive } : {}),
          ...(venueId ? { venueId } : {}),
        },
      });

      dispatch(slice.actions.getPromotionsSuccess(response.data));

      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load promotions."),
        ),
      );

      throw error;
    }
  };
}

export function getFranchisePromotion({ franchiseId, promotionId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/franchise/promotion/${promotionId}`,
        {
          params: {
            franchiseId,
          },
        },
      );

      dispatch(slice.actions.getPromotionSuccess(response.data));

      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load promotion."),
        ),
      );

      throw error;
    }
  };
}

export function createFranchisePromotion({ franchiseId, payload }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.post(
        `${backendUrl}/franchise/promotion`,
        payload,
        {
          params: { franchiseId },
        },
      );

      dispatch(slice.actions.savePromotionSuccess(response.data));

      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to create promotion."),
        ),
      );

      throw error;
    }
  };
}

export function updateFranchisePromotion({
  franchiseId,
  promotionId,
  payload,
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.patch(
        `${backendUrl}/franchise/promotion/${promotionId}`,
        payload,
        {
          params: {
            franchiseId,
          },
        },
      );

      dispatch(slice.actions.savePromotionSuccess(response.data));

      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to update promotion."),
        ),
      );

      throw error;
    }
  };
}

export function deleteFranchisePromotion({ franchiseId, promotionId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      await axios.delete(`${backendUrl}/franchise/promotion/${promotionId}`, {
        params: {
          franchiseId,
        },
      });

      dispatch(slice.actions.savePromotionSuccess(null));

      return true;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to delete promotion."),
        ),
      );

      throw error;
    }
  };
}
